//un comment once this added to ccomponent-adapter-interfaces
import {
  Logger,
  formatError,
  getLocalStorageItem,
  getMSTeamsContext,
} from "../../../../packages/logger/src";
import {
  APP,
  CALLING_BACKEND,
  IMetricsData,
  INTEGRATION,
  IUserData,
  METRIC
} from "../../../adapter-interfaces/src/IMetricsAdapter";
import { msTeamsIntegration, appVersion } from "../utils/MetricUtils";

// Maximum number of retries when Webex is not initialized
const MAX_RETRY_ATTEMPTS = 5;
// Initial delay in milliseconds
const INITIAL_RETRY_DELAY = 1000;

/**
 * Returns true once the Webex SDK service catalog has resolved a usable URL
 * for the `metrics` API. Without this check the SDK's internal services
 * registry returns `undefined`, and the underlying transport happily fires
 * `https://undefined/metrics/api/v1/clientmetrics` — wasting a network round
 * trip and polluting devtools/logs.
 *
 * The metrics URL lives in the SDK's *postauth* catalog, which is populated
 * during device registration. The SDK's `'ready'` event (which sets
 * `window.isWebexInitalized`) fires *before* device registration completes,
 * so we must check the service URL directly instead of relying solely on the
 * init flag.
 */
function isMetricsServiceReady(): boolean {
  const services = (window as any)?.webex?.internal?.services;
  if (!services || typeof services.get !== 'function') return false;
  let url: string | undefined;
  try {
    url = services.get('metrics');
  } catch {
    return false;
  }
  // A resolved catalog entry is always an absolute https URL — anything else
  // (undefined, "undefined", empty string, "https://undefined/...") means the
  // catalog hasn't been fetched yet for this session.
  if (typeof url !== 'string' || !url) return false;
  if (url.includes('undefined')) return false;
  return /^https?:\/\//i.test(url);
}

/**
 * Best-effort opportunistic wait for the postauth service catalog. Resolves
 * silently regardless of outcome so callers can fall through to their own
 * retry/backoff logic.
 */
async function tryAwaitPostauthCatalog(timeoutMs: number): Promise<void> {
  const services = (window as any)?.webex?.internal?.services;
  if (!services || typeof services.waitForCatalog !== 'function') return;
  try {
    await Promise.race([
      services.waitForCatalog('postauth'),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  } catch {
    // Swallow — the calling code will re-check readiness explicitly.
  }
}

async function submitMetricsWithRetry(
  metricObject: IUserData,
  retryCount: number = 0
): Promise<any> {
  try {
    const ready = window?.isWebexInitalized && isMetricsServiceReady();
    if (!ready) {
      if (retryCount >= MAX_RETRY_ATTEMPTS) {
        Logger.info(
          `WebexMetricsService.submitMetrics(): Webex/metrics service not ready after ${MAX_RETRY_ATTEMPTS} attempts (initialized=${!!window?.isWebexInitalized}, metricsServiceReady=${isMetricsServiceReady()}). Skipping metric submission for event: ${metricObject.metricEvent}`
        );
        return null; // Silently fail to prevent flooding
      }

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
      Logger.info(
        `WebexMetricsService.submitMetrics(): Webex not ready (initialized=${!!window?.isWebexInitalized}, metricsServiceReady=${isMetricsServiceReady()}). Retry ${retryCount + 1}/${MAX_RETRY_ATTEMPTS} after ${delay}ms for event: ${metricObject.metricEvent}`
      );
      // If the SDK is initialized but the catalog isn't fetched yet, ride on
      // its waitForCatalog promise instead of busy-polling — this typically
      // resolves before the backoff timer would have fired.
      if (window?.isWebexInitalized) {
        await tryAwaitPostauthCatalog(delay);
      } else {
        await new Promise(f => setTimeout(f, delay));
      }
      return submitMetricsWithRetry(metricObject, retryCount + 1);
    }

    const orgName = await (
      window as any
    )?.webex?.internal?.device?.orgName;
    const userProperties = await constructPayload(metricObject, orgName);
    const eventPayload = {
      metricInput: { userContext: userProperties},
      isTestClient: false,
      client_timestamp: metricObject.client_timestamp
        ? metricObject.client_timestamp
        : msTeamsIntegration,
      trackingId: metricObject.trackingId
        ? metricObject.trackingId
        : msTeamsIntegration,
      key: metricObject.key ? metricObject.key : msTeamsIntegration,
      value: metricObject.value ? metricObject.value : msTeamsIntegration,
    };
    const Metrics = { type: [METRIC.BUSINESS, METRIC.BEHAVIORAL, METRIC.OPERATIONAL], eventPayload: eventPayload, tags: userProperties };
    const response = await (
      window as any
    )?.webex?.internal?.metrics?.submitClientMetrics(
      metricObject.metricEvent,
      Metrics
    );
    return response;
  } catch (error) {
    Logger.error(
      `WebexMetricsService.submitMetrics(): got error: '${formatError(error)}'.`
    );
    throw error;
  }
}

export async function submitMetrics(
  metricObject: IUserData,
): Promise<any> {
  return submitMetricsWithRetry(metricObject, 0);
}

export async function constructPayload(
  metricObject: IUserData, orgName: string
): Promise<IMetricsData> {
  const context = await getMSTeamsContext();
  const payload: IMetricsData = {
    metricEvent: metricObject.metricEvent,
    appName: APP.WEBEXTEAMS,
    appSessionId: context.app.sessionId,
    appVersion: appVersion,
    entityId: context.page.id,
    sessionId: context.app.host.sessionId,
    userObjectId: context.user?.id,
    loginHint: context.user?.loginHint,
    hostClientType: context.app.host.clientType,
    tid: context.user?.tenant?.id,
    integrationName: INTEGRATION.MSTEAMS,
    messageId: metricObject.messageId,
    responseStatus: metricObject.responseStatus,
    callingBackend: getLocalStorageItem<string>(CALLING_BACKEND.WEBEX_USER)
      ? getLocalStorageItem<string>(CALLING_BACKEND.WEBEX_USER)
      : "",
    contentSize: metricObject.contentSize,
    executionTime: metricObject.executionTime,
    presenceServerApiReturnCode: metricObject.presenceServerApiReturnCode
      ? metricObject.presenceServerApiReturnCode
      : undefined,
    presenceServerApiLatency: metricObject.presenceServerApiLatency
      ? metricObject.presenceServerApiLatency
      : "0",
    voiceMailDuration: metricObject.voiceMailDuration
      ? metricObject.voiceMailDuration
      : "0",
    voicemailListLatency: metricObject.voicemailListLatency
      ? metricObject.voicemailListLatency
      : "0",
    recentCallsListLatency: metricObject.recentCallsListLatency
      ? metricObject.recentCallsListLatency
      : "0",
    callForwardSettingsLatency: metricObject.callForwardSettingsLatency
      ? metricObject.callForwardSettingsLatency
      : "0",
    voicemailSettingsLatency: metricObject.voicemailSettingsLatency
      ? metricObject.voicemailSettingsLatency
      : "0",
    callForwardSettingsApiReturnCode: metricObject.callForwardSettingsApiReturnCode
      ? metricObject.callForwardSettingsApiReturnCode
      : undefined,
    voicemailSettingsApiReturnCode: metricObject.voicemailSettingsApiReturnCode
      ? metricObject.voicemailSettingsApiReturnCode
      : undefined,
    voiceMailContentErrorResponseCode:
      metricObject.voiceMailContentErrorResponseCode
        ? metricObject.voiceMailContentErrorResponseCode
        : undefined,
    callMadeSection: metricObject.callMadeSection
    ? metricObject.callMadeSection
    : undefined,
    activeTab: metricObject.activeTab
    ? metricObject.activeTab
    : undefined,
    callType: metricObject.callType
    ? metricObject.callType
    : undefined,
    orgName: orgName ? orgName : undefined,
    key: metricObject.key ? metricObject.key : msTeamsIntegration,
    downloadRedirectSection: metricObject.downloadRedirectSection
    ? metricObject.downloadRedirectSection
    : undefined,
    phoneServiceStatus: metricObject.phoneServiceStatus ?  metricObject.phoneServiceStatus : undefined,
    callingDockStatus: metricObject.callingDockStatus ? metricObject.callingDockStatus : undefined,
    e911Status: metricObject.e911Status ? metricObject.e911Status : undefined,
    dataTransferSubscriptionStatus: metricObject.dataTransferSubscriptionStatus ? metricObject.dataTransferSubscriptionStatus : undefined,
    dataTransferErrorStatus: metricObject.dataTransferErrorStatus ? metricObject.dataTransferErrorStatus : undefined
  };
  return payload;
}

//delete once this added to ccomponent-adapter-interfaces
// export interface IMetricsData {
//   appName: string;
//   appSessionId: string;
//   entityId: string;
//   sessionId: string;
//   userObjectId?: string;
//   metricEvent: string;
//   loginHint?: string;
//   hostClientType: string;
//   tid?: string;
//   numberOfSpeedDials?: string;
//   integrationName: string;
//   messageId?: string;
//   responseStatus?: string;
//   callingBackend: string;
//   contentSize?: string;
//   executionTime?: string;
// }
// export interface IUserData {
//   metricEvent: string;
//   messageId?: string;
//   integrationName: INTEGRATION;
//   responseStatus?: string;
//   contentSize?: string;
//   executionTime?: string;
//   client_timestamp?: string;
//   trackingId?: string;
//   key?: string;
//   value?: string;
// }

// export enum METRIC {
//   BUSINESS = 'business',
// }

// export enum INTEGRATION {
//   MSTEAMS = 'msteams',
// }

// export enum APP {
//   WEBEXTEAMS = 'webexteams',
// }

// export enum CALLING_BACKEND {
//   WEBEX_USER = 'webex_userCallingBackend',
// }

// export interface IMetricsAdaptor {}
